/**
 * shared.ts
 * Compras (REQUI) module contracts — domain vocabulary shared across every
 * section: closed status/event unions (case/item/PO/quote-line status,
 * event classes/types), soft-control reason codes (deviation/cancel/
 * justification), units, item kinds/modalidad, demand origin, priorities,
 * and the module's error codes (COMPRAS_ERROR_CODES).
 *
 * refactor/compras-by-section — pure move out of index.ts (no shape/behavior change). Section files (openspec/
 * arquitectura/convencion-modulo.md §2/§3):
 *   - requisiciones.ts       — case/item creation + responses, pipeline,
 *                              my-cases, seed tooling
 *   - cotizaciones.ts        — RFQ rounds, comparative matrix, consolidation
 *   - ordenes-compra.ts      — purchase order summaries/detail/approval
 *   - catalogo-articulos.ts  — item master (Artículos) + normalization queue
 *   - stock.ts               — warehouses, stock balances/movements/dispatch
 *   - domain-events.ts       — POST /procurement/.../events discriminated
 *                              union member schemas
 *   - dashboard.ts           — Tablero de Compras (cross-cutting)
 */
import { z } from 'zod';

export const CASE_STATUSES = [
  'borrador',
  'pendiente_aprobacion',
  'requisitada',
  'en_cotizacion',
  'adjudicacion_parcial',
  'adjudicada',
  'en_ejecucion',
  'cerrada',
  'cancelada',
] as const;
export const CaseStatusSchema = z.enum(CASE_STATUSES);
export type CaseStatus = z.infer<typeof CaseStatusSchema>;

/**
 * case_items.status — the item's own state machine (SDD §4 T0-T25).
 *
 * `pendiente_aprobacion_oc_n1` / `pendiente_aprobacion_oc_n1n2` /
 * `pendiente_aprobacion_oc_n2` — 2026-08-31-requi-aprobacion-oc-por-monto
 * (design.md D1). Sit between `adjudicado` and `en_oc`: every awarded group
 * is held here before a `purchase_orders` row ever exists. Contracts-only in
 * Phase 1 — the transition engine wiring (T14h1/T14h2/T27/T27e/T27b/T28/T28b)
 * is Phase 2.
 */
export const ITEM_STATUSES = [
  'borrador',
  'pendiente',
  'en_cotizacion',
  'cotizado',
  'desierto',
  'requiere_aprobacion_tecnica',
  'adjudicado',
  'pendiente_aprobacion_oc_n1',
  'pendiente_aprobacion_oc_n1n2',
  'pendiente_aprobacion_oc_n2',
  'en_oc',
  'equipo_entregado',
  'alquiler_activo',
  'devuelto',
  'recibido',
  'cancelado',
] as const;
export const ItemStatusSchema = z.enum(ITEM_STATUSES);
export type ItemStatus = z.infer<typeof ItemStatusSchema>;

/** purchase_orders.status. */
export const PO_STATUSES = [
  'emitida',
  'confirmada',
  'rechazada_proveedor',
  'en_entrega',
  'completa',
  'anulada',
] as const;
export const PoStatusSchema = z.enum(PO_STATUSES);
export type PoStatus = z.infer<typeof PoStatusSchema>;

/**
 * purchase_orders.payment_method (migration 0150) — wizard paso ④ mockup
 * "Forma de pago". Pura metadata administrativa, distinta del cronograma de
 * purchase_order_payment_term (dias/porcentaje, resumido como "Detalle").
 */
export const PURCHASE_ORDER_PAYMENT_METHODS = ['contado', 'cuenta_corriente', 'cheque', 'transferencia'] as const;
export const PurchaseOrderPaymentMethodSchema = z.enum(PURCHASE_ORDER_PAYMENT_METHODS);
export type PurchaseOrderPaymentMethod = z.infer<typeof PurchaseOrderPaymentMethodSchema>;
/** "Forma de pago" labels — the OC detail screen and the printed order. */
export const PURCHASE_ORDER_PAYMENT_METHOD_LABELS: Record<PurchaseOrderPaymentMethod, string> = {
  contado: 'Contado',
  cuenta_corriente: 'Cuenta corriente',
  cheque: 'Cheque',
  transferencia: 'Transferencia',
};

/** quote_lines.status — distinct from MATRIX_CELL_STATUSES below, which excludes 'descartada'. */
export const QUOTE_LINE_STATUSES = [
  'cotizada',
  'desestimada',
  'sustituto_propuesto',
  'parcial',
  'vencida',
  'adjudicada',
  'descartada',
] as const;
export const QuoteLineStatusSchema = z.enum(QUOTE_LINE_STATUSES);
export type QuoteLineStatus = z.infer<typeof QuoteLineStatusSchema>;

/** case_events.class. */
export const EVENT_CLASSES = ['rutina', 'decision', 'anomalia'] as const;
export const EventClassSchema = z.enum(EVENT_CLASSES);
export type EventClass = z.infer<typeof EventClassSchema>;

/** case_events.type — one per row of SDD §4's TRANSITIONS table (T0-T25). */
export const ITEM_EVENTS = [
  'aprobar_requisicion', // T0
  'desestimar_en_aprobacion', // T0b
  'incluir_en_ronda', // T1
  'auto_split_rondas', // T1a
  'cotizacion_recibida', // T2
  'cotizacion_cargada_manual', // T2m
  'cotizacion_parcial_recibida', // T3
  'item_desestimado', // T4
  'sustituto_propuesto', // T5
  'ronda_cerrada', // T6
  'sustituto_aprobado', // T7
  'sustituto_rechazado', // T8
  'adjudicar_item', // T9
  'adjudicar_cantidad_parcial', // T10
  'reabrir_en_nueva_ronda', // T11
  'compra_directa_justificada', // T12
  'cancelar_item', // T13 / T17
  'incluir_en_oc', // T14
  'oc_rechazada_por_proveedor', // T15
  'recepcion_registrada', // T16
  'anular_linea_oc', // T18
  'adjudicar_contra_acuerdo', // T19
  'equipo_entregado', // T20
  'extender_alquiler', // T21
  'devolver_equipo', // T22
  'mover_a_pool', // T23
  'incluir_en_consolidacion', // T24
  'revalidar_demanda', // T25
  'revertir_adjudicacion', // T26
  'disolver_consolidacion', // T29
  // 2026-08-31-requi-aprobacion-oc-por-monto (design.md D1) — held groups
  // between adjudicado and en_oc. T14h1/T14h2 are system-fired (no HTTP
  // payload); T27/T27e share one event name disambiguated by matchesFrom
  // (Phase 2). Contracts-only in Phase 1.
  'retener_oc_n1', // T14h1
  'retener_oc_n1n2', // T14h2
  'aprobar_oc_n1', // T27 / T27e
  'rechazar_oc_n1', // T27b
  'aprobar_oc_n2', // T28
  'rechazar_oc_n2', // T28b
] as const;
export const ItemEventSchema = z.enum(ITEM_EVENTS);
export type ItemEvent = z.infer<typeof ItemEventSchema>;

/**
 * T9 — motivo de desvío (openspec/requi/anexo-oc-y-desvios.md §1). Single
 * source of truth (was previously redeclared in the frontend's
 * stateLabel.ts) — moved here so the Zod boundary and the UI selector can
 * never drift apart. "Un clic resuelve el 100% de los casos — ningún código
 * exige texto, incluido `otro`" (anexo §1, revised this session): the code
 * ALONE satisfies `deviationReason`; an optional free-text clarification
 * travels separately as `deviationNote` on the same event payload.
 */
export const DEVIATION_REASONS = [
  'condiciones_pago',
  'plazo_entrega',
  'calidad_marca',
  'proveedor_homologado',
  'continuidad',
  'disponibilidad_stock',
  'otro',
] as const;
export const DeviationReasonSchema = z.enum(DEVIATION_REASONS);
export type DeviationReasonCode = z.infer<typeof DeviationReasonSchema>;

/**
 * T0b/T13/T17 (and, per this session's brief, T4's item_desestimado) —
 * `motivo`. Closed one-click list, replaces free text (section 4 of this
 * session's pass — "controles blandos, trazabilidad dura": a guard never
 * demands mandatory free text, only a selection the actor can make alone).
 */
export const CANCEL_REASONS = [
  'ya_no_se_necesita',
  'presupuesto_no_aprobado',
  'duplicado',
  'cambio_de_alcance',
  'hay_en_stock',
  'error_de_carga',
  'otro',
] as const;
export const CancelReasonSchema = z.enum(CANCEL_REASONS);
export type CancelReasonCode = z.infer<typeof CancelReasonSchema>;

/**
 * T12 (compra_directa_justificada) — motivo de la compra directa. Lista
 * cerrada, un clic ("controles blandos, trazabilidad dura" — un guard nunca
 * exige texto libre obligatorio). A diferencia de DEVIATION_REASONS (donde
 * la nota es SIEMPRE opcional, para cualquier código), acá invariante 8
 * histórica exigía SIEMPRE detalle escrito — esa exigencia se hereda por
 * `otro` únicamente: es el único código que además requiere
 * `justificationNote` (CompraDirectaJustificadaEventSchema below).
 */
export const JUSTIFICATION_REASONS = [
  'urgencia_operativa',
  'proveedor_unico',
  'monto_menor',
  'continuidad_obra',
  'desierto_reiterado',
  'otro',
] as const;
export const JustificationReasonSchema = z.enum(JUSTIFICATION_REASONS);
export type JustificationReasonCode = z.infer<typeof JustificationReasonSchema>;

// ---------------------------------------------------------------------------
// Case creation — POST /procurement/cases
// ---------------------------------------------------------------------------

/**
 * Alta de solicitud (alta-solicitud.md §3.4) — "Unidad ... Nunca `un` tipeado
 * a mano": lista cerrada, no texto libre. Same const-array + z.enum + exported
 * type pattern as DEVIATION_REASONS/CANCEL_REASONS above. Labels live in the
 * frontend's stateLabel.ts, same split as those two vocabularies.
 */
export const UNITS = [
  'un',
  'kg',
  'm',
  'm2',
  'm3',
  'l',
  'caja',
  'rollo',
  'par',
  'juego',
  'hora',
  'global',
] as const;
export const UnitSchema = z.enum(UNITS);
export type Unit = z.infer<typeof UnitSchema>;

/**
 * Short suffix shown next to a quantity ("200 un", "12,5 m²"). Unlike the
 * full labels (frontend stateLabel.ts), the abbreviations have two consumers
 * — every qty column in the UI and the supplier-facing OC PDF — so they live
 * here, once. An unknown unit (legacy free text) falls back to itself.
 */
const UNIT_ABBRS: Record<Unit, string> = {
  un: 'un',
  kg: 'kg',
  m: 'm',
  m2: 'm²',
  m3: 'm³',
  l: 'l',
  caja: 'caja',
  rollo: 'rollo',
  par: 'par',
  juego: 'jgo',
  hora: 'h',
  global: 'gl',
};

export function unitAbbr(unit: string): string {
  return UNIT_ABBRS[unit as Unit] ?? unit;
}

export const ITEM_KINDS = ['bien', 'servicio'] as const;

/**
 * Migration 0136 — split out of ITEM_KINDS: alquiler is the MODALIDAD of a
 * case_item line (the same bien/servicio can be bought or rented), never a
 * kind of article. Same pattern as DEMAND_ORIGINS below.
 */
export const MODALIDADES = ['compra', 'alquiler'] as const;
export const ModalidadSchema = z.enum(MODALIDADES);
export type Modalidad = z.infer<typeof ModalidadSchema>;

export const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

/**
 * alta-solicitud.md §2/§4 — "Origen de la demanda": define el centro de
 * costo y si hay cliente. `orden_de_trabajo` shows Cliente/N° OT in the form;
 * `consumo_interno` and `reposicion_stock` don't. `reposicion_stock` reverses
 * the previously recorded "no hay módulo de stock" decision — see
 * `openspec/changes/requi-inventario-stock/proposal.md` (REQ-44): the pañol
 * can now raise a solicitud directly from a stock balance.
 */
export const DEMAND_ORIGINS = ['orden_de_trabajo', 'consumo_interno', 'reposicion_stock'] as const;
export const DemandOriginSchema = z.enum(DEMAND_ORIGINS);
export type DemandOrigin = z.infer<typeof DemandOriginSchema>;

// ---------------------------------------------------------------------------
// Error codes specific to this module (mirrors RRHH_ERROR_CODES pattern).
// ---------------------------------------------------------------------------
export const COMPRAS_ERROR_CODES = {
  INVALID_STATE_TRANSITION: 'COMPRAS_INVALID_STATE_TRANSITION',
  NO_ITEMS_TO_APPROVE: 'COMPRAS_NO_ITEMS_TO_APPROVE',
  /** Artículos ABM — uq_item_master_code_tenant (migration 0137). */
  ITEM_MASTER_CODE_CONFLICT: 'COMPRAS_ITEM_MASTER_CODE_CONFLICT',
  /** PromoteToItemMasterUseCase (cola de normalización) — itemIds vacío. */
  NO_ITEMS_SELECTED: 'COMPRAS_NO_ITEMS_SELECTED',
  /** requi-inventario-stock — uq_warehouse_name_tenant (migration 0156, case-insensitive on lower(name)). */
  WAREHOUSE_NAME_CONFLICT: 'COMPRAS_WAREHOUSE_NAME_CONFLICT',
  /** requi-inventario-stock PR 2 — REQ-56, a movement would leave stock_balances.qty < 0. */
  INSUFFICIENT_STOCK: 'COMPRAS_INSUFFICIENT_STOCK',
  /** requi-inventario-stock PR 2 — REQ-57, kind !== 'bien' can never move stock. */
  ITEM_NOT_STOCKABLE: 'COMPRAS_ITEM_NOT_STOCKABLE',
  /** odd/tasks/operator-panol.md S1 — the scanned code already belongs to another article. */
  BARCODE_TAKEN: 'COMPRAS_BARCODE_TAKEN',
  /**
   * 2026-09-04-requi-solicitud-vehiculo (compras-item-asset-link AL-2.2,
   * design D2) — a gated rubro's item is missing `assetId`, OR the supplied
   * `assetId` does not resolve for the tenant via `IAssetLookupPort.lookupById`.
   * Same code for BOTH cases on purpose — a distinct 404 for "doesn't
   * resolve" would leak whether another tenant owns that uuid.
   */
  ASSET_REQUIRED: 'COMPRAS_ASSET_REQUIRED',
  /**
   * requi-consolidacion-slice-c — the legacy case-scoped PDF route
   * (`GET /procurement/cases/:caseId/purchase-orders/:orderId/orden-de-compra.pdf`)
   * refuses a round-owned (consolidated, multi-SA) purchase order: rendering
   * only the requesting SA's lines against the OC's FULL cross-SA total would
   * be a non-reconciling financial document. Use the PO-centric route
   * (`GET /procurement/purchase-orders/:id/orden-de-compra.pdf`) instead.
   */
  PURCHASE_ORDER_IS_CONSOLIDATED: 'COMPRAS_PURCHASE_ORDER_IS_CONSOLIDATED',
} as const;
